import React, {ReactNode, useCallback, useState, useEffect} from 'react'

// Components
import {Button} from '@/components/ui/button'
import {Form, FormCheckbox, FormField, FormInput, FormPhone} from '@/components/ui/form'
import AccountErrorSummary from '../accountErrorSummary'
import Deadline from './deadline'
import {TurnstileWidget} from '~/app/utils/cloudflare/turnstile/turnstile'

// Form dependencies and helpers
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm} from 'react-hook-form'
import {z} from 'zod'

// Hooks and helper
import {cn} from '@/lib/utils'
import {AuthHelpers, useAuthHelper} from '@salesforce/commerce-sdk-react'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {useClientSitePreferences} from 'hec/context/site-preferences'
import SvgLogoFsaPerksNavy from 'hec/assets/icons/logo-fsa-perks-navy'
import SvgLogoHsaPerksNavy from 'hec/assets/icons/logo-hsa-perks-navy'
import {RegistrationFormLabels} from 'hec/builder/hooks/useGetAccountFormData'
import {useSitePrefs} from 'hec/hooks/useSitePrefs'
import {defineMessage, useIntl} from 'react-intl'
import {handleAccountCreationForSegment} from '~/app/utils/segment/eventHandlers'
import useTpaMarketingConsent from '~/app/hooks/use-tpa-marketing-consent'
import useUpdateProfile from 'hec/hooks/commerce-sdk/useUpdateProfile'

interface CreateAccountFormProps {
    closeDrawer: () => void
    className?: string
    formLabels: RegistrationFormLabels
    perksEnrollmentTermsContent: ReactNode
    termsAndConditionsContent: ReactNode
}

export const phoneValidator = z
    .string()
    .regex(/^(\+1\s?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/, {
        message: 'Please enter a valid phone number'
    })
    // leave only digits and +1 country code
    .transform((val) => {
        const digits = val.replace(/[^\d+]/g, '')
        return digits.startsWith('+1') ? `+1${digits.slice(2)}` : digits
    })
    .refine((val) => val.length === 10 || (val.startsWith('+1') && val.length === 12), {
        message: 'Phone number must have 10 digits (or 12 with +1 country code)'
    })

const CreateAccountForm = ({
    closeDrawer,
    className = '',
    formLabels,
    perksEnrollmentTermsContent,
    termsAndConditionsContent,
    ...remainingProps
}: CreateAccountFormProps) => {
    const {showTermsAndConditions} = useSitePrefs()
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {formatMessage} = useIntl()
    const register = useAuthHelper(AuthHelpers.Register)
    const [disabledSubmit, setDisabledSubmit] = useState(false)
    const {site} = useMultiSite()
    const siteId = site.id
    const prefs = useClientSitePreferences()
    const turnstileEnabled = !isServer && prefs?.CloudFlareConfigs?.cloudFlareTurnstileEnabled
    const [isButtonDisabled, setIsButtonDisabled] = useState(!!turnstileEnabled)

    useEffect(() => {
        setIsButtonDisabled(turnstileEnabled)
    }, [turnstileEnabled])
    const {setExplicitConsent, isTpaUser} = useTpaMarketingConsent()
    const EMAIL_ALREADY_REGISTERED = defineMessage({
        defaultMessage: 'An account with this email already exists',
        id: 'login_page.error.email_already_registered'
    })

    const zObject: any = {
        email: z
            .string({
                required_error: 'This field is required'
            })
            .min(1, 'This field is required')
            .email({message: 'Please enter a valid email address'}),
        password: z
            .string({
                required_error: 'This field is required'
            })
            .min(1, 'This field is required')
            .regex(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
                'Password must contain 8 characters, letters, numbers, mixed case and 1 special character'
            ),
        confirm: z.string({
            required_error: 'This field is required'
        }),
        phone: phoneValidator.optional(),
        deadline: z.string().optional(),
        sms: z.boolean().default(false).optional(),
        enroll: z.boolean().default(true).optional()
    }

    if (showTermsAndConditions) {
        zObject.terms = z.boolean().refine((val) => val === true, {
            message: 'Please accept the Terms of Use and Privacy Notice'
        })
    }

    const FormSchema = z.object(zObject).refine((data) => data.password === data.confirm, {
        message: "Passwords don't match",
        path: ['confirm']
    })

    const form = useForm<z.infer<typeof FormSchema>>({
        resolver: zodResolver(FormSchema),
        defaultValues: {
            sms: false,
            enroll: true,
            terms: showTermsAndConditions ? false : undefined
        }
    })

    const updateProfile = useUpdateProfile()

    const onSubmit = async (data: z.infer<typeof FormSchema>) => {
        const tandc = showTermsAndConditions ? data.terms : false
        const body: any = {
            customer: {
                lastName: ' ',
                email: data.email,
                login: data.email,
                c_smsAcknowledgment: data.sms,
                c_tandcAcknowledgment: tandc
            },
            password: data.password
        }

        const payloadData = {
            trigger: 'register' as const,
            smsOptIn: data.sms,
            loyaltyOptIn: data.enroll,
            deadline: data.deadline ? [data.deadline] : '',
            phone: data.phone || '',
            tandc: tandc
        }

        if (data.phone) {
            body.customer.phoneHome = data.phone
        }

        if (data.deadline) {
            body.customer.c_deadline = data.deadline
        }

        if (isTpaUser) {
            await setExplicitConsent()
        }

        handleAccountCreationForSegment({
            siteName: siteId,
            email: data.email,
            emailChecked: true,
            phone: data.phone,
            smsChecked: data.sms,
            placement: 'create-account'
        })

        try {
            await register.mutateAsync(body, {
                onError: (err: any) => {
                    err.response
                        .json()
                        .then((resObj: {detail: string}) => {
                            if (/login.*in.*use/i.test(resObj.detail)) {
                                form.setError('email', {
                                    type: 'manual',
                                    message: formatMessage(EMAIL_ALREADY_REGISTERED)
                                })
                            }
                        })
                        .catch((jsonError: any) => {
                            console.error('Failed to parse error response:', jsonError)
                        })
                }
            })
            await updateProfile.mutateAsync(payloadData)
            closeDrawer()
        } catch (e) {
            form.setError('global', {type: 'manual', message: formatMessage(API_ERROR_MESSAGE)})
        }
    }

    const {email, password, confirmPassword, phone, smsOptIn} = formLabels || {}

    const handleTurnstileSuccess = useCallback(() => {
        setIsButtonDisabled(false)
    }, [])

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
        if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
            e.preventDefault()
        }
    }, [])
    return (
        <Form {...form} data-testid="createAccountForm">
            <AccountErrorSummary
                errors={form.formState.errors}
                data-testid={'createAccountForm-AccountErrorSummary'}
            />
            <TurnstileWidget
                data-testid="createAccountForm-TurnstileWidget"
                onSuccess={handleTurnstileSuccess}
            />
            <form
                {...remainingProps}
                onSubmit={async (e) => {
                    if (showTermsAndConditions) {
                        setDisabledSubmit(!form.getValues('terms'))
                    }
                    await form.handleSubmit(onSubmit)(e)
                }}
                className={cn(className, 'space-y-6')}
            >
                <FormField
                    control={form.control}
                    name="email"
                    render={({field}) => (
                        <FormInput
                            label={email || ''}
                            inputType="email"
                            autoComplete="username"
                            fieldProps={field}
                            required={true}
                            validate={() => form.trigger('email')}
                            data-testid="createAccountForm-FormInput-email"
                        />
                    )}
                    data-testid="createAccountForm-FormField-email"
                />
                <div className="grid grid-cols-2 gap-2.5">
                    <div className="col-span-2 sm:col-span-1">
                        <FormField
                            control={form.control}
                            name="password"
                            render={({field}) => (
                                <FormInput
                                    label={password || ''}
                                    inputType="password"
                                    fieldProps={field}
                                    autoComplete="new-password"
                                    required={true}
                                    tooltipMsg={`<ul class="normal-case mt-2"><li class="mb-2">Minimum password length: 8 characters</li><li class="mb-2">Must contain letters</li><li class="mb-2">Must contain numbers</li><li class="mb-2">Letters must be mixed case (ex: HsA)</li><li class="mb-2">Minimum of 1 special character ($%/()[]{}=?!.,-_*|+~#)</li></ul>`}
                                    validate={() => form.trigger('password')}
                                    data-testid="createAccountForm-FormInput-password"
                                />
                            )}
                            data-testid="createAccountForm-FormField-password"
                        />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                        <FormField
                            control={form.control}
                            name="confirm"
                            render={({field}) => (
                                <FormInput
                                    label={confirmPassword || ''}
                                    inputType="password"
                                    autoComplete="new-password"
                                    fieldProps={field}
                                    required={true}
                                    validate={() => form.trigger('confirm')}
                                    data-testid="createAccountForm-FormInput-confirm"
                                />
                            )}
                            data-testid="createAccountForm-FormField-confirm"
                        />
                    </div>
                </div>
                <FormField
                    control={form.control}
                    name="phone"
                    render={({field}) => (
                        <FormPhone
                            label={phone || ''}
                            fieldProps={field}
                            validate={() => form.trigger('phone')}
                            onKeyDown={handleKeyDown}
                            autoComplete="tel"
                            data-testid="createAccountForm-FormInput-phone"
                        />
                    )}
                    data-testid="createAccountForm-FormField-phone"
                />
                {siteId === 'FSASTORE' && (
                    <FormField
                        control={form.control}
                        name="deadline"
                        render={({field}) => (
                            <Deadline
                                label="FSA Deadline"
                                field={field}
                                data-testid="createAccountForm-Deadline-deadline"
                            />
                        )}
                        data-testid="createAccountForm-FormField-deadline"
                    />
                )}
                {showTermsAndConditions ? (
                    <FormField
                        control={form.control}
                        name="terms"
                        render={({field}) => (
                            // TODO: This should probably be converted to a property config, or moved into a CMS builder section.
                            <FormCheckbox
                                label={termsAndConditionsContent}
                                fieldProps={field}
                                onCheckedChange={(checked) => setDisabledSubmit(!checked)}
                                data-testid="createAccountForm-FormCheckbox-terms"
                            />
                        )}
                        data-testid="createAccountForm-FormField-terms"
                    />
                ) : (
                    <p className="body-md lg:text-sm lg:leading-[1.375rem]">
                        {termsAndConditionsContent}
                    </p>
                )}
                <FormField
                    control={form.control}
                    name="sms"
                    render={({field}) => (
                        <FormCheckbox
                            label={smsOptIn || ''}
                            fieldProps={field}
                            data-testid="createAccountForm-FormCheckbox-sms"
                        />
                    )}
                    data-testid="createAccountForm-FormField-sms"
                />

                <FormField
                    control={form.control}
                    name="enroll"
                    render={({field}) => (
                        <div>
                            <a
                                className="w-[6.25rem] block mb-3"
                                href="/loyalty-perks"
                                rel="noreferrer"
                            >
                                {siteId === 'HSASTORE' && <SvgLogoHsaPerksNavy />}
                                {siteId === 'FSASTORE' && <SvgLogoFsaPerksNavy />}
                            </a>
                            <FormCheckbox
                                label={perksEnrollmentTermsContent}
                                fieldProps={field}
                                data-testid="createAccountForm-FormCheckbox-enroll"
                            />
                        </div>
                    )}
                    data-testid="createAccountForm-FormField-enroll"
                />
                <div className="flex flex-col">
                    <Button
                        type="submit"
                        ctaType="button"
                        variant="primary"
                        className="w-[18.75rem] mx-auto mb-24 lg:mb-0"
                        disabled={disabledSubmit || (turnstileEnabled && isButtonDisabled)}
                        data-testid="createAccountForm-Button"
                    >
                        Create Account
                    </Button>
                </div>
            </form>
        </Form>
    )
}

export default CreateAccountForm
